/**
 * RequirementsNotes — document-like view shell.
 *
 * Owns: level switch (Outline/Summary/Full), grouping, DnD setup, insertAfter.
 * Delegates block rendering to notes/NotesChapter → NotesNode → {Initiative|Epic|Story}.
 */
import { useCallback } from 'react'
import type { Requirement, RequirementTheme } from '@ispot/shared'
import { useRequirementsViewStore, type NotesLevel } from '@/stores/requirementsViewStore'
import { useGroupedRequirements } from '@/hooks/useGroupedRequirements'
import { useBlockReorder, type BlockMeta } from '@/hooks/useBlockReorder'
import { useInsertAfter } from '@/hooks/useInsertAfter'
import { NotesChapter } from './notes/NotesChapter'
import { extractPersonaFromGroupId } from './notes/notesShared'
import type { CreateBlockInput } from './AdderBlock'

interface PersonaMeta { name: string; role?: string; color?: string; initials?: string; photo?: string }

interface Props {
  requirements: Requirement[]
  themes: RequirementTheme[]
  personaIndex?: Map<string, PersonaMeta>
  onUpdate: (id: string, updates: Partial<Requirement>) => Promise<unknown>
  onCreate: (input: CreateBlockInput) => Promise<{ id: string }>
  onDelete: (id: string) => Promise<void>
  onReorder: (parentId: string | null, orderedIds: string[]) => Promise<void>
}

const LEVELS: Array<{ value: NotesLevel; label: string; title: string }> = [
  { value: 'outline', label: 'Outline',  title: 'Section headings only' },
  { value: 'summary', label: 'Summary',  title: 'Sections + epics' },
  { value: 'full',    label: 'Full',     title: 'Everything' },
]

export function RequirementsNotes({ requirements, themes, personaIndex, onUpdate, onCreate, onDelete, onReorder }: Props) {
  const { groups } = useGroupedRequirements({ requirements, themes, personaIndex })
  const level = useRequirementsViewStore(s => s.notesLevel)
  const setLevel = useRequirementsViewStore(s => s.setNotesLevel)

  const getSiblings = useCallback((meta: BlockMeta): BlockMeta[] => {
    return requirements
      .filter(r => r.type === meta.type && (r.parentId ?? null) === (meta.parentId ?? null))
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map(r => ({ type: r.type as BlockMeta['type'], id: r.id, parentId: r.parentId, sortOrder: r.sortOrder }))
  }, [requirements])

  const dnd = useBlockReorder({ onCommit: onReorder, getSiblings })
  const insertAfter = useInsertAfter({ requirements, onCreate, onReorder })

  return (
    <div
      style={{
        maxWidth: 760,
        margin: '0 auto',
        padding: '12px 24px 160px',
      }}
    >
      <div className="flex items-center gap-2.5 mb-6">
        <div
          role="tablist"
          aria-label="Detail level"
          className="inline-flex gap-0.5 p-[3px]"
          style={{
            background: 'var(--color-warm-100)',
            border: '1px solid var(--color-border)',
            borderRadius: 8,
            fontSize: 12,
          }}
        >
          {LEVELS.map(({ value, label, title }) => {
            const active = level === value
            return (
              <button
                key={value}
                role="tab"
                aria-selected={active}
                onClick={() => setLevel(value)}
                title={title}
                aria-label={`${label} — ${title}`}
                className="inline-flex items-center gap-1 px-2 py-[3px] rounded-[4px] text-[11.5px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2"
                style={{
                  background: active ? 'var(--color-bg-card)' : 'transparent',
                  color: active ? 'var(--color-fg)' : 'var(--color-fg-muted)',
                  boxShadow: active ? '0 0 0 0.5px var(--color-border-strong)' : undefined,
                  ['--tw-ring-color' as string]: 'var(--color-accent)',
                }}
              >
                {label}
              </button>
            )
          })}
        </div>
      </div>

      {groups.length === 0 && (
        <div className="text-[13px]" style={{ color: 'var(--color-fg-muted)' }}>
          No requirements match the current filters.
        </div>
      )}

      {groups.map(g => (
        <NotesChapter
          key={g.id}
          group={g}
          onUpdate={onUpdate}
          onCreate={onCreate}
          onDelete={onDelete}
          insertAfter={insertAfter}
          dnd={dnd}
          personaSessionId={extractPersonaFromGroupId(g.id)}
          personaIndex={personaIndex}
          themes={themes}
        />
      ))}
    </div>
  )
}
